<template>
  <v-container class="h-full">
    <v-row class="justify-end flex-0">
      <v-col cols="4">
        <v-text-field
          v-model="search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          class="mb-4"
          clearable
        />
      </v-col>
    </v-row>
    <v-data-table-server
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      v-model:sort-by="sortBy"
      :headers="headers"
      :items="items"
      :items-length="itemsLength"
      :items-per-page-options="[10, 25, 50, 100]"
      :loading="loading"
      :search="search"
    >
      <template #item.email="{ item }">
        <p v-text="item.email || '(no email)'" />
        <p class="font-light whitespace-nowrap" v-text="item.id" />
      </template>
      <template #item.onboarded="{ item }">
        <div class="d-flex justify-center">
          <v-icon v-if="item.onboarded" key="v-icon-1" color="green">
            mdi-check-circle
          </v-icon>
          <v-icon v-else key="v-icon-2" color="red">
            mdi-close-circle
          </v-icon>
        </div>
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex justify-center">
          <v-btn
            variant="flat"
            size="32"
            color="primary"
            icon="mdi-pencil"
            @click="viewUser(item)"
          />
        </div>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script lang="ts">
import { computed, defineComponent, markRaw, onMounted, ref, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useApiStore } from '@/stores/api'

import { RouteNames } from '@/router'

import type { User } from '@policyfly/protobuf/pfbingo'
import type { DataTableHeader } from 'vuetify'
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs'

export default defineComponent({
  name: 'UserList',

  setup () {
    // users
    const apiStore = useApiStore()
    const users = shallowRef<User[]>([])
    const loading = ref(true)
    async function loadUsers (): Promise<void> {
      loading.value = true
      const response = await apiStore.listUsers({})
      users.value = response.users
      loading.value = false
    }
    onMounted(async () => {
      await loadUsers()
    })

    // table
    const headers: DataTableHeader<User>[] = markRaw([
      { title: 'Email / ID', key: 'email', value: 'email', sortable: true },
      { title: 'First Name', key: 'firstName', value: 'firstName', sortable: true },
      { title: 'Last Name', key: 'lastName', value: 'lastName', sortable: true },
      { title: 'Role', key: 'role', value: 'role', sortable: true },
      { title: 'Visibility', key: 'visibility', value: 'visibility', sortable: true },
      { title: 'Onboarded', key: 'onboarded', value: 'onboarded', align: 'center', sortable: false },
      { title: 'Actions', key: 'actions', value: 'actions', sortable: false, align: 'center' },
    ])
    const search = ref('')
    watch(search, () => {
      page.value = 1
    })

    // items
    const itemsPerPage = ref(10)
    const page = ref(1)
    const sortBy = ref<SortItem[]>([])
    const filteredItems = computed(() => {
      if (!search.value) return users.value
      return users.value.filter((user) => {
        return Object.values(user).some((val) =>
          String(val).toLowerCase().includes(search.value.toLowerCase()),
        )
      })
    })
    const itemsLength = computed(() => filteredItems.value.length)
    const sortedItems = computed(() => {
      return filteredItems.value.slice().sort((a, b) => {
        for (const sortItem of sortBy.value) {
          const aValue = String(a[sortItem.key as keyof typeof a])
          const bValue = String(b[sortItem.key as keyof typeof b])
          if (aValue < bValue) return sortItem.order === 'desc' ? 1 : -1
          if (aValue > bValue) return sortItem.order === 'desc' ? -1 : 1
        }
        return 0
      })
    })
    const items = computed(() => {
      const offset = (page.value - 1) * itemsPerPage.value
      return sortedItems.value.slice(offset, offset + itemsPerPage.value)
    })

    const router = useRouter()
    async function viewUser (user: User): Promise<void> {
      await router.push({ name: RouteNames.USER_EDIT, params: { id: user.id } })
    }

    return {
      // users
      loading,
      // table
      headers,
      search,
      // items
      itemsPerPage,
      page,
      sortBy,
      itemsLength,
      items,
      viewUser,
    }
  },
})
</script>
